import { useState, useEffect, useContext } from 'react';
import { Grid } from '@mui/material';
import { OpenInNewOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getPath } from '../../pages/Pages';
import { getErrorMessage } from '../../lib/utils';
import { useCrud, useLoader } from '../../lib/hooks';
import {
    AppContext,
    SttDialog,
    SttFormDate,
    SttFormText,
    SttFormTime,
} from '../../sporttia/all';
import ErrorsDialog, {
    BusyError,
} from '../../pages/sc/busySchedules/ErrorsDialog';
import { PERMISSIONS } from '../../lib/permissions';

type Creator = {
    name: string;
};

type Schedule = {
    id: number;
};

type Slot = {
    schedule?: Schedule;
};

type BusySchedule = {
    id?: number;
    name?: string;
    ini?: string;
    end?: string;
    timeini?: string;
    timeend?: string;
    creator?: Creator;
    slot?: Slot;
};

type Facility = {
    id: number;
    name: string;
};

type BusyResponse = {
    busy: BusySchedule;
};

type ScheduleResponse = {
    fails?: unknown[];
};

type BusyScheduleDialogProps = {
    item: BusySchedule;
    facility?: Facility;
    onSave?: () => void;
    onClose?: () => void;
};

export default function BusyScheduleDialog({
    item,
    facility,
    onSave,
    onClose,
}: BusyScheduleDialogProps): React.ReactElement | null {
    const { t } = useTranslation();
    const cxt = useContext(AppContext)!;
    const navigate = useNavigate();
    const [fails, setFails] = useState<unknown[]>([]);
    const [
        busySchedule,
        setBusySchedule,
        setPropertyRaw,
        Get,
        Post,
        Put,
        Delete,
    ] = useCrud<BusySchedule>(item, false);
    const [creating, setCreating] = useState(false);
    const [closingAllStatus, setClosingAllStatus] = useState<string>('idle');
    const [savingStatus, setSavingStatus] = useState<string>('idle');
    const [, loader] = useLoader([closingAllStatus, savingStatus]);

    // Wrapper to adapt useCrud setProperty to form component onChange signature
    const setProperty = ({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }) => {
        if (!name) return;
        setPropertyRaw({
            name: name as keyof BusySchedule,
            value: value as BusySchedule[keyof BusySchedule],
        });
    };

    /**
     * loadBusySchedule
     */
    function loadBusySchedule(): void {
        if (!item.id) return;

        Get<BusyResponse>(`/busies/${item.id}`).then((r: any) => {
            setBusySchedule({
                ...r.busy,
                timeini: item.timeini,
                timeend: item.timeend,
                slot: r.busy.slot,
            });
        });
    }

    // init
    useEffect(() => {
        setCreating(!item.id);

        if (item.id) {
            loadBusySchedule();
        }
    }, [item.id]);

    /**
     * saveAndClose
     */
    function saveAndClose(): void {
        onSave?.();
        onClose?.();
    }

    /**
     * saveBusySchedule
     */
    function saveBusySchedule(): void {
        if (!busySchedule) return;

        setSavingStatus('loading');

        // Creating
        if (creating) {
            Post<ScheduleResponse>(
                `/scs/${cxt?.sc?.id}/busies/schedules`,
                busySchedule,
            )
                .then((response: any) => {
                    setSavingStatus('idle');
                    if (response?.fails && response.fails.length > 0) {
                        setFails(response.fails);
                    } else {
                        cxt.showMessage(
                            'S',
                            t('busySchedules.creatingInBackground'),
                        );
                        saveAndClose();
                    }
                })
                .catch((error: unknown) => {
                    setSavingStatus('idle');
                    cxt.showMessage('E', getErrorMessage(error));
                });

            // Updating
        } else if (item.id) {
            Put(`/busies/${item.id}`, {
                name: busySchedule.name,
                timeini: busySchedule.timeini,
                timeend: busySchedule.timeend,
            })
                .then(() => {
                    setSavingStatus('idle');
                    saveAndClose();
                })
                .catch((error: unknown) => {
                    setSavingStatus('idle');
                    cxt.showMessage('E', getErrorMessage(error));
                });
        }
    }

    /**
     * Liberate
     */
    function liberate(): void {
        if (!item.id) return;

        Delete(`/busies/${item.id}`).then(() => {
            cxt.showMessage('S', t('Deleted'));
            saveAndClose();
        });
    }

    /**
     * Close all sport center.
     */
    function closeAllSportCenter(): void {
        setClosingAllStatus('loading');
        cxt?.api('POST', `/schedules/busies/all`, {
            params: {
                date: busySchedule?.ini,
            },
            success: (response: ScheduleResponse) => {
                setClosingAllStatus('idle');
                if (response?.fails && response.fails.length > 0) {
                    setFails(response.fails);
                } else {
                    cxt.showMessage(
                        'S',
                        t('busySchedules.creatingInBackground'),
                    );
                    saveAndClose();
                }
            },
            error: (error: unknown) => {
                setClosingAllStatus('idle');
                cxt.showMessage('E', getErrorMessage(error));
            },
        });
    }

    if (!item || !busySchedule) {
        return null;
    }

    return (
        <>
            <SttDialog
                open={!!item}
                onClose={onClose}
                title={creating ? t('NewBusy') : t('Busy')}
                subtitle={
                    busySchedule.creator &&
                    `${t('createdBy')}: ${busySchedule.creator.name}`
                }
                content={
                    <>
                        {loader}
                        <Grid container spacing={3}>
                            {/* Basic Info */}
                            <Grid size={{ xs: 12 }}>
                                <SttFormText
                                    name="name"
                                    caption={t('Name')}
                                    defVal={busySchedule.name ?? ''}
                                    onChange={setProperty}
                                />
                            </Grid>

                            {facility && (
                                <Grid size={{ xs: 12 }}>
                                    <SttFormText
                                        disabled
                                        name="facility"
                                        caption={t('Facility')}
                                        defVal={facility.name}
                                    />
                                </Grid>
                            )}

                            {/* Period - Start */}
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <SttFormDate
                                    disabled={!creating}
                                    name="ini"
                                    caption={t('Begin')}
                                    defVal={busySchedule.ini}
                                    onChange={setProperty}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <SttFormTime
                                    name="timeini"
                                    caption={t('TimeIni')}
                                    defVal={busySchedule.timeini}
                                    min="06:00"
                                    max="23:45"
                                    timeRangeOptions={{ includeMaxTime: true }}
                                    interval={10}
                                    onChange={setProperty}
                                />
                            </Grid>

                            {/* Period - End */}
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <SttFormDate
                                    disabled={!creating}
                                    name="end"
                                    caption={t('Until')}
                                    defVal={busySchedule.end}
                                    onChange={setProperty}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <SttFormTime
                                    name="timeend"
                                    caption={t('TimeEnd')}
                                    defVal={busySchedule.timeend}
                                    min="06:00"
                                    max="23:45"
                                    timeRangeOptions={{ includeMaxTime: true }}
                                    interval={10}
                                    onChange={setProperty}
                                />
                            </Grid>
                        </Grid>
                    </>
                }
                buttons={[
                    {
                        type: 'delete',
                        caption: t('CloseAllSportcenter'),
                        onClick: closeAllSportCenter,
                        permission: PERMISSIONS.CLOSURE.CREATE,
                        disabled: closingAllStatus === 'loading',
                    },
                    {
                        type: 'save',
                        onClick: saveBusySchedule,
                        permission: creating
                            ? PERMISSIONS.CLOSURE.CREATE
                            : PERMISSIONS.CLOSURE.MODIFY,
                        disabled: savingStatus === 'loading',
                    },
                    {
                        show: !creating,
                        type: 'delete',
                        caption: t('Liberate'),
                        onClick: liberate,
                        permission: PERMISSIONS.CLOSURE.DELETE,
                    },
                    {
                        show: !creating && !!busySchedule?.slot?.schedule?.id,
                        icon: <OpenInNewOutlined fontSize="small" />,
                        caption: t('timetable.gotoBusySchedule'),
                        onClick: () => {
                            const scheduleId = busySchedule?.slot?.schedule?.id;
                            if (scheduleId) {
                                // Close dialog first before navigating
                                onClose?.();
                                navigate(
                                    getPath('busySchedule', { id: scheduleId }),
                                );
                            }
                        },
                    },
                    {
                        type: 'close',
                        onClick: onClose,
                    },
                ]}
            />
            {fails.length > 0 && (
                <ErrorsDialog
                    errors={fails as BusyError[]}
                    onClose={() => {
                        setFails([]);
                        saveAndClose();
                    }}
                />
            )}
        </>
    );
}
